import { Injectable } from '@angular/core';
import { HttpHeaders } from '@angular/common/http';
import {
	AccountModel,
	ApiService,
	UserLoginModel,
	MobileAuthenticatorModel,
	RefreshTokensModel,
	AuthenticatorStatus,
	ResetPasswordModel,
	EnableMobileAuthenticatorModel,
	ImpersonateModel,
	EnableEmailAuthenticatorModel,
	ResetEmailAuthenticator
} from "@nstep-common/core";

import { Observable, tap } from 'rxjs';

@Injectable({
	providedIn: 'root'
})
export class AccountService {
	constructor(private apiService: ApiService) { }

	logIn(model: UserLoginModel): Observable<AccountModel> {
		return this.apiService.post(AccountModel, 'account/log-in', model, {
			headers: new HttpHeaders({
				'Time-Zone': Intl.DateTimeFormat().resolvedOptions().timeZone
			})
		});
	}

	refresh(model: RefreshTokensModel): Observable<RefreshTokensModel> {
		return this.apiService.post(RefreshTokensModel, 'account/refresh', model);
	}

	logOut(): Observable<any> {
		return this.apiService.postNoContent('account/log-out')
			.pipe(tap(() => this.apiService.invalidateEntireCache()));
	}

	passwordReset(model: ResetPasswordModel): Observable<AccountModel> {
		return this.apiService.post(AccountModel, 'account/password/reset', model, {
			headers: new HttpHeaders({
				'Time-Zone': Intl.DateTimeFormat().resolvedOptions().timeZone
			})
		});
	}

	getTwoFactor(): Observable<AuthenticatorStatus> {
		return this.apiService.get(AuthenticatorStatus, 'account/2f');
	}

	loginEnableMobileAuthenticator(model: UserLoginModel): Observable<AccountModel> {
		return this.apiService
			.post(AccountModel, 'account/2f/mobile/login/enable', model, {
				headers: new HttpHeaders({
					'Time-Zone': Intl.DateTimeFormat().resolvedOptions().timeZone
				})
			})
			.pipe(tap(() => {
				this.invalidateTwoFactorCache();
			}));
	}

	enableMobileAuthenticator(model: EnableMobileAuthenticatorModel): Observable<any> {
		return this.apiService.postNoContent('account/2f/mobile/enable', model)
			.pipe(tap(() => {
				this.invalidateTwoFactorCache();
			}));
	}

	resetMobileAuthenticator(): Observable<MobileAuthenticatorModel> {
		return this.apiService.post(MobileAuthenticatorModel, 'account/2f/mobile/reset')
			.pipe(tap(() => {
				this.invalidateTwoFactorCache();
			}));
	}

	sendEmailAuthenticatorCode(model: UserLoginModel, email: string): Observable<any> {
		const data = {
			...model,
			email
		};

		return this.apiService.postNoContent('account/2f/email/login/send', data);
	}

	loginEnableEmailAuthenticator(model: UserLoginModel, email: string): Observable<AccountModel> {
		const data = {
			...model,
			email
		};

		return this.apiService
			.post(AccountModel, 'account/2f/email/login/enable', data, {
				headers: new HttpHeaders({
					'Time-Zone': Intl.DateTimeFormat().resolvedOptions().timeZone
				})
			})
			.pipe(tap(() => {
				this.invalidateTwoFactorCache();
			}));
	}

	enableEmailAuthenticator(model: EnableEmailAuthenticatorModel): Observable<any> {
		return this.apiService.postNoContent('account/2f/email/enable', model)
			.pipe(tap(() => {
				this.invalidateTwoFactorCache();
			}));
	}

	resetEmailAuthenticator(model: ResetEmailAuthenticator): Observable<any> {
		return this.apiService.postNoContent('account/2f/email/reset', model)
			.pipe(tap(() => {
				this.invalidateTwoFactorCache();
			}));
	}

	impersonables(query: string):  Observable<ImpersonateModel[]> {
		return this.apiService.get([ImpersonateModel], `account/impersonables?query=${encodeURIComponent(query)}`);
	}

	impersonate(model: ImpersonateModel):  Observable<AccountModel> {
		return this.apiService.post(AccountModel, 'account/impersonate', model, {
			headers: new HttpHeaders({
				'Time-Zone': Intl.DateTimeFormat().resolvedOptions().timeZone
			})
		});
	}

	invalidateTwoFactorCache() {
		this.apiService.invalidatePartialMatchCache('account/2f');
	}
}
